import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/composables/useApi'
import type { User } from '@/types'
import { secureStorage, performSecurityCheck } from '@/utils/secureStorage'

interface LoginCredentials {
  email: string
  password: string
  remember?: boolean
}

interface RegisterData {
  customer_type: 'individual' | 'professional'
  first_name: string
  last_name: string
  phone?: string
  email: string
  // Address: required for business customers only (see RegisterRequest)
  customer_street?: string
  customer_city?: string
  customer_postal_code?: string
  customer_country?: string
  company_name?: string
  vat_number?: string
  password: string
  password_confirmation?: string
}

interface AuthResponse {
  user: User
  token: string
  token_type: string
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(secureStorage.getToken())
  const loading = ref(false)

  // Computed properties
  const isAuthenticated = computed(() => !!user.value && !!token.value)
  const isAdmin = computed(() => user.value?.role === 'admin')

  // Format credit balance for display
  const creditBalanceFormatted = computed(() => {
    if (!user.value?.credit_balance_cents) return '€0,00'
    const euros = user.value.credit_balance_cents / 100
    return '€' + euros.toFixed(2).replace('.', ',')
  })

  // Authorization is handled by the api instance interceptor

  // Haalt de XSRF-cookie op vóór de eerste POST, zodat een koude paginalading
  // (direct naar /login of /register) niet met een 419 CSRF-fout faalt op
  // stateful Sanctum-domeinen. Niet-blokkerend: zonder cookie werkt de
  // token-gebaseerde login alsnog zodra een eerdere API-call de cookie zette.
  const primeCsrfCookie = async () => {
    try {
      const origin = import.meta.env.VITE_API_BASE_URL || 'https://portal.boenkop.be'
      await api.get('/sanctum/csrf-cookie', { baseURL: origin })
    } catch {
      // bewust genegeerd
    }
  }

  // Actions
  const login = async (credentials: LoginCredentials) => {
    loading.value = true

    try {
      await primeCsrfCookie()
      const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/login', credentials)

      if (response.data.success) {
        const { user: userData, token: userToken } = response.data.data

        // Store user and token
        user.value = userData
        token.value = userToken

        // Store token in localStorage
        secureStorage.setToken(userToken)

        // Perform security check after authentication
        performSecurityCheck()
      }
    } catch (error) {
      // Clear any existing auth state on error
      await logout()

      if (error instanceof Error && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.data?.message) {
          throw new Error(axiosError.response.data.message, { cause: error })
        }
      }
      throw new Error('Inloggen mislukt', { cause: error })
    } finally {
      loading.value = false
    }
  }

  const register = async (data: RegisterData) => {
    loading.value = true

    try {
      await primeCsrfCookie()
      const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/register', data)

      if (response.data.success) {
        const { user: userData, token: userToken } = response.data.data

        // Store user and token
        user.value = userData
        token.value = userToken

        // Store token in localStorage
        secureStorage.setToken(userToken)

        // Perform security check after authentication
        performSecurityCheck()
      }
    } catch (error) {
      // Clear any existing auth state on error
      await logout()

      if (error instanceof Error && 'response' in error) {
        const axiosError = error as any
        if (axiosError.response?.data?.errors) {
          // Validation errors
          throw { errors: axiosError.response.data.errors }
        }

        if (axiosError.response?.data?.message) {
          throw new Error(axiosError.response.data.message, { cause: error })
        }
      }

      throw new Error('Registratie mislukt', { cause: error })
    } finally {
      loading.value = false
    }
  }

  const clearAuthState = () => {
    user.value = null
    token.value = null
    secureStorage.removeToken()
  }

  const logout = async () => {
    loading.value = true

    try {
      // If we have a token, try to logout on the server
      if (token.value) {
        await api.post('/auth/logout')
      }
    } catch (error) {
      // Even if server logout fails, we should clear local state
      // Server logout failed, but we clear local state anyway
    } finally {
      // Clear local state
      clearAuthState()
      loading.value = false
    }
  }

  const fetchUser = async () => {
    if (!token.value) return

    loading.value = true

    // Don't automatically logout on fetchUser failure
    // Let the caller decide what to do (errors propagate to the caller)
    try {
      const response = await api.get<{ success: boolean; data: { user: User } }>('/auth/me')

      if (response.data.success) {
        user.value = response.data.data.user
      }
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    user,
    token,
    loading,

    // Getters
    isAuthenticated,
    isAdmin,
    creditBalanceFormatted,

    // Actions
    login,
    register,
    logout,
    clearAuthState,
    fetchUser
  }
})